import { useCallback, useEffect, useRef, useState } from "react";
import type { HistoryEntry, KyInstance } from "@repo/ui/chat";
import type { AgentSession, ListSessionsResponse, RenameSessionRequest } from "@repo/ui/chat";

function toHistoryEntry(raw: AgentSession): HistoryEntry {
  return {
    id: raw.session_id,
    title: raw.title,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  };
}

export function useAgentSessions(api: KyInstance): {
  sessions: HistoryEntry[];
  loading: boolean;
  error: string | undefined;
  refetch: () => void;
  rename: (id: string, title: string) => Promise<void>;
  remove: (id: string) => Promise<void>;
} {
  const [sessions, setSessions] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [refetchNonce, setRefetchNonce] = useState(0);
  /**
   * Bumped by every accepted mutation. A list response that crosses a mutation is a pre-write
   * snapshot (it would resurrect a deleted row or an old title), so it is dropped, not merged.
   */
  const mutationEpochRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const issuedAtEpoch = mutationEpochRef.current;

    async function load() {
      setLoading(true);
      setError(undefined);
      try {
        const body = await api.get("sessions").json<ListSessionsResponse>();
        if (cancelled || mutationEpochRef.current !== issuedAtEpoch) return;
        setSessions((body.sessions ?? []).map(toHistoryEntry));
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load sessions");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [api, refetchNonce]);

  const refetch = useCallback(() => setRefetchNonce((n) => n + 1), []);

  // Non-optimistic: the list only moves once the server accepts, and a failure is rethrown
  // for the initiating row to render with a retry.
  const rename = useCallback(
    async (id: string, title: string) => {
      const json: RenameSessionRequest = { title };
      await api.put(`sessions/${encodeURIComponent(id)}`, { json });
      mutationEpochRef.current += 1;
      setSessions((current) =>
        current.map((session) => (session.id === id ? { ...session, title } : session)),
      );
    },
    [api],
  );

  const remove = useCallback(
    async (id: string) => {
      await api.delete(`sessions/${encodeURIComponent(id)}`);
      mutationEpochRef.current += 1;
      setSessions((current) => current.filter((session) => session.id !== id));
    },
    [api],
  );

  return { sessions, loading, error, refetch, rename, remove };
}
